import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  MilestoneMap1,
  MapInvitation,
  Objective1,
  UserProfile1,
  SignUpLink1,
  Collaborator,
} from '../types';
import type { Human } from '@mm-shared/types/Human';
import type { HumanRelationship } from '@mm-shared/types/HumanRelationship';
import { handleAsyncError } from '../utils/errorHandling';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import { upsertItem } from '../utils/upsertItem';
import type { RootState } from './store';
import { UpdateData, OperationType } from './map3PendingSlice';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { DependencyRecord, getDependencyRecords } from '../map_dependencies/utils/dependencyUtils';
import {
  startMilestoneMapListener,
  startObjectivesListener,
  cleanupMapListeners,
  cleanupAllCollaborationListeners,
  startCollaboratorsListener,
  stopCollaboratorsListener,
  startHumansListener,
  stopHumansListener,
  startHumanRelationshipsListener,
  stopHumanRelationshipsListener,
} from './collaborationThunks';
import { getListenerId } from '../collaboration/utils/listenerIds';
import { debugLog } from '../utils/debug';

// Real-time sync types
type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

interface DataState {
  milestoneMaps: MilestoneMap1[];
  objectives: Objective1[];
  humans: Human[];
  humanRelationships: HumanRelationship[];
  userProfile: UserProfile1;
  isLoading: boolean;
  error: string | null;
  lastSyncTimestamp: number | null;
  searchResults: {
    parents: Objective1[];
    children: Objective1[];
  };
  signUpLink: SignUpLink1 | null;
  dependencyRecords: DependencyRecord[];
  // Real-time sync state
  connectionStatus: ConnectionStatus;
  activeListenerIds: string[];
  syncError: string | null;
  lastConnectionAttempt: number | null;
  reconnectAttempts: number;
  // Collaboration tracking
  activeCollaborators: Record<string, Collaborator[]>; // mapUuid -> collaborators
}

const initialState: DataState = {
  milestoneMaps: [],
  objectives: [],
  humans: [],
  humanRelationships: [],
  userProfile: {
    uid: '',
    accountType: 'alpha_deactivated',
    userName: '',
  },
  isLoading: false,
  error: null,
  lastSyncTimestamp: null,
  searchResults: {
    parents: [],
    children: [],
  },
  signUpLink: null,
  dependencyRecords: [],
  // Real-time sync initial state
  connectionStatus: 'disconnected',
  activeListenerIds: [],
  syncError: null,
  lastConnectionAttempt: null,
  reconnectAttempts: 0,
  // Collaboration tracking initial state
  activeCollaborators: {},
};

interface UserLookupResult {
  email: string;
  exists: boolean;
  uid?: string;
  userName?: string;
  firstName?: string;
  lastName?: string;
}

// Thunks
export const fetchMilestoneMap = createAsyncThunk<
  MilestoneMap1,
  string,
  {
    rejectValue: string;
  }
>('map3Data/fetchMilestoneMap', async (idOrShortId, { getState, rejectWithValue }) => {
  try {
    // Check existing state first
    const state = getState() as RootState;
    const existingMap = state.map3Data.milestoneMaps.find(
      (map) => map.shortId === idOrShortId || map.uuid === idOrShortId,
    );
    if (existingMap) {
      return existingMap;
    }

    const db = getFirestore();
    const mapsCollection = collection(db, 'milestoneMaps');

    // 1. If input looks like a uuid (36 chars, dashes), try direct doc lookup and query by uuid field
    const isUuid = idOrShortId.length === 36 && idOrShortId.includes('-');
    if (isUuid) {
      // Try direct doc lookup
      const directRef = doc(db, 'milestoneMaps', idOrShortId);
      const directSnap = await getDoc(directRef);
      if (directSnap.exists()) {
        const data = directSnap.data();
        if (!data.uuid || !data.shortId || !data.title || !data.objectiveUuids) {
          throw new Error(`Invalid milestone map idOrShortId: "${idOrShortId}"`);
        }
        return {
          ...data,
          usersWithAccess: data.usersWithAccess || [],
          pendingMapInvitations: data.pendingMapInvitations || [],
          createdBy: typeof data.createdBy === 'object' ? data.createdBy.userName : data.createdBy,
        } as MilestoneMap1;
      }
      // Try query by uuid field
      const qUuid = query(mapsCollection, where('uuid', '==', idOrShortId));
      const uuidSnapshot = await getDocs(qUuid);
      if (!uuidSnapshot.empty) {
        const data = uuidSnapshot.docs[0].data();
        if (!data.uuid || !data.shortId || !data.title || !data.objectiveUuids) {
          throw new Error(`Invalid milestone map data structure for uuid: "${idOrShortId}"`);
        }
        return {
          ...data,
          usersWithAccess: data.usersWithAccess || [],
          pendingMapInvitations: data.pendingMapInvitations || [],
          createdBy: typeof data.createdBy === 'object' ? data.createdBy.userName : data.createdBy,
        } as MilestoneMap1;
      }
    }

    // 2. If input is a shortId (10 chars), try query by shortId field
    const isShortId = idOrShortId.length === 10;
    if (isShortId) {
      const qShort = query(mapsCollection, where('shortId', '==', idOrShortId));
      const shortIdSnapshot = await getDocs(qShort);
      if (!shortIdSnapshot.empty) {
        const data = shortIdSnapshot.docs[0].data();
        if (!data.uuid || !data.shortId || !data.title || !data.objectiveUuids) {
          throw new Error(`Invalid milestone map data structure for shortId: "${idOrShortId}"`);
        }
        return {
          ...data,
          usersWithAccess: data.usersWithAccess || [],
          pendingMapInvitations: data.pendingMapInvitations || [],
          createdBy: typeof data.createdBy === 'object' ? data.createdBy.userName : data.createdBy,
        } as MilestoneMap1;
      }
    }

    // 3. If input is shorter than uuid and not found by shortId, try querying for uuids that start with the input
    if (!isUuid && idOrShortId.length < 36) {
      const qStartsWith = query(
        mapsCollection,
        where('uuid', '>=', idOrShortId),
        where('uuid', '<', idOrShortId + '\uf8ff'),
      );
      const startsWithSnapshot = await getDocs(qStartsWith);
      if (!startsWithSnapshot.empty) {
        const data = startsWithSnapshot.docs[0].data();
        if (!data.uuid || !data.shortId || !data.title || !data.objectiveUuids) {
          throw new Error(`Invalid milestone map data structure for uuid prefix "${idOrShortId}"`);
        }
        return {
          ...data,
          usersWithAccess: data.usersWithAccess || [],
          pendingMapInvitations: data.pendingMapInvitations || [],
          createdBy: typeof data.createdBy === 'object' ? data.createdBy.userName : data.createdBy,
        } as MilestoneMap1;
      }
    }

    return rejectWithValue(`Milestone map with id or shortId "${idOrShortId}" not found`);
  } catch (error) {
    return rejectWithValue(handleAsyncError(error, 'fetchMilestoneMap'));
  }
});

export const fetchObjectives = createAsyncThunk<
  Objective1[],
  string[],
  {
    rejectValue: string;
  }
>('map3Data/fetchObjectives', async (objectiveUuids, { getState, rejectWithValue }) => {
  try {
    const state = getState() as RootState;
    const existingObjectives = state.map3Data.objectives;
    const missingUuids = objectiveUuids.filter(
      (uuid) => !existingObjectives.find((obj) => obj.uuid === uuid),
    );

    // If all objectives are already loaded in local state, return them.
    if (missingUuids.length === 0) {
      return objectiveUuids.map((uuid) => existingObjectives.find((obj) => obj.uuid === uuid)!);
    }

    const objectives: Objective1[] = [];
    const db = getFirestore();

    // Fetch missing objectives in batches of 10 (Firestore limit)
    for (let i = 0; i < missingUuids.length; i += 10) {
      const batch = missingUuids.slice(i, i + 10);
      const q = query(collection(db, 'objectives'), where('uuid', 'in', batch));
      const querySnapshot = await getDocs(q);

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (!data.uuid || !data.text) {
          throw new Error(`Invalid objective data structure for uuid "${doc.id}"`);
        }
        objectives.push(data as Objective1);
      });
    }

    return objectives;
  } catch (error) {
    return rejectWithValue(handleAsyncError(error, 'fetchObjectives'));
  }
});

/**
 * Fetches Human and HumanRelationship documents from Firestore for the given
 * set of objective UUIDs. Called during map load so the owner combobox is
 * populated and owner display works on page refresh.
 *
 * Strategy:
 *  1. Query humanRelationships WHERE targetUuid IN objectiveUuids (batched ≤10)
 *  2. Collect unique humanUuids from those relationships
 *  3. Query humans WHERE uuid IN humanUuids (batched ≤10)
 */
export const fetchHumansAndRelationshipsForMap = createAsyncThunk<
  { humans: Human[]; humanRelationships: HumanRelationship[] },
  string[], // objectiveUuids
  { rejectValue: string }
>(
  'map3Data/fetchHumansAndRelationshipsForMap',
  async (objectiveUuids, { rejectWithValue }) => {
    try {
      if (objectiveUuids.length === 0) {
        return { humans: [], humanRelationships: [] };
      }

      const firestoreDb = getFirestore();
      const allRelationships: HumanRelationship[] = [];

      // Firestore 'in' queries are limited to 10 values at a time
      for (let i = 0; i < objectiveUuids.length; i += 10) {
        const batch = objectiveUuids.slice(i, i + 10);
        const q = query(
          collection(firestoreDb, 'humanRelationships'),
          where('targetUuid', 'in', batch),
        );
        const snapshot = await getDocs(q);
        snapshot.forEach((d) => {
          allRelationships.push(d.data() as HumanRelationship);
        });
      }

      // Collect unique humanUuids referenced by these relationships
      const humanUuids = Array.from(new Set(allRelationships.map((r) => r.humanUuid)));

      const allHumans: Human[] = [];
      for (let i = 0; i < humanUuids.length; i += 10) {
        const batch = humanUuids.slice(i, i + 10);
        const q = query(
          collection(firestoreDb, 'humans'),
          where('uuid', 'in', batch),
        );
        const snapshot = await getDocs(q);
        snapshot.forEach((d) => {
          allHumans.push(d.data() as Human);
        });
      }

      return { humans: allHumans, humanRelationships: allRelationships };
    } catch (error) {
      return rejectWithValue(handleAsyncError(error, 'fetchHumansAndRelationshipsForMap'));
    }
  },
);

/**
 * Fetches all MilestoneMaps that contain a given objective UUID.
 * Uses `array-contains` query on the `objectiveUuids` field.
 * Results are upserted into state.milestoneMaps (accumulating cache).
 */
export const fetchMilestoneMapsByObjective = createAsyncThunk<
  MilestoneMap1[],
  string,
  { rejectValue: string }
>('map3Data/fetchMilestoneMapsByObjective', async (objectiveUuid, { rejectWithValue }) => {
  try {
    const db = getFirestore();
    const q = query(
      collection(db, 'milestoneMaps'),
      where('objectiveUuids', 'array-contains', objectiveUuid),
    );
    const snapshot = await getDocs(q);
    const maps: MilestoneMap1[] = [];
    snapshot.forEach((doc) => {
      maps.push(doc.data() as MilestoneMap1);
    });
    return maps;
  } catch (error) {
    return rejectWithValue(handleAsyncError(error, 'fetchMilestoneMapsByObjective'));
  }
});

export const loadMapDataAsync = createAsyncThunk(
  'map3Data/loadMapData',
  async (shortId: string, { dispatch, getState, rejectWithValue }) => {
    try {
      // Set connecting status at start
      dispatch(setConnectionStatus('connecting'));
      debugLog('[RealTimeSync] Starting map data load for shortId:', shortId);

      const milestoneMap = await dispatch(fetchMilestoneMap(shortId)).unwrap();
      const objectives = await dispatch(fetchObjectives(milestoneMap.objectiveUuids)).unwrap();

      // Load humans and relationships for all objectives in this map so the
      // owner combobox is populated and owner display works on page refresh.
      await dispatch(
        fetchHumansAndRelationshipsForMap(milestoneMap.objectiveUuids),
      ).unwrap();

      // Get all maps for dependency resolution
      const state = getState() as RootState;
      const allMaps = state.map3Data.milestoneMaps;

      // Get dependency records
      const dependencyRecords = getDependencyRecords(milestoneMap, objectives, allMaps);

      // Set up real-time listeners using collaboration thunks
      // TODO: Fix TypeScript issues and enable these calls in next section
      // try {
      //   await dispatch(startMilestoneMapListener({ mapUuid: milestoneMap.uuid }));
      //   await dispatch(startObjectivesListener({
      //     mapUuid: milestoneMap.uuid,
      //     objectiveUuids: milestoneMap.objectiveUuids,
      //   }));
      //   console.log('[RealTimeSync] Collaboration listeners established successfully');
      // } catch (listenerError) {
      //   console.warn('[RealTimeSync] Failed to establish collaboration listeners:', listenerError);
      // }

      // Update connection status to connected
      dispatch(setConnectionStatus('connected'));
      debugLog('[RealTimeSync] Map data loaded and real-time listener established');

      return { milestoneMap, objectives, dependencyRecords };
    } catch (error) {
      console.error('[RealTimeSync] Failed to load map data:', error);
      dispatch(setConnectionStatus('error'));
      dispatch(setSyncError((error as any)?.message || 'Failed to load map data'));
      return rejectWithValue(handleAsyncError(error, 'loadMapDataAsync'));
    }
  },
);

export const loadCurrentUserProfile = createAsyncThunk(
  'map3Data/loadCurrentUserProfile',
  async (_, { rejectWithValue }) => {
    try {
      const auth = getAuth();
      await new Promise((resolve) => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
          unsubscribe();
          resolve(user);
        });
      });

      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.log('loadCurrentUserProfile - No authenticated user');
        return rejectWithValue('No authenticated user');
      }

      const userDoc = await getDoc(doc(db, 'userProfiles', currentUser.uid));

      // If the user exists in Auth but not in Firestore, return a partial user object
      if (!userDoc.exists()) {
        return {
          uid: currentUser.uid,
          email: currentUser.email ? [currentUser.email] : [],
          // Note: If the got interrupted during signup following an invite link
          // alpha_pending here may downgrade their access and land them in pergatory.
          // May need to update handling here after dust settles around invite links.
          //
          // TODO: Check to see if currentUser.email was sent an invite. If so, set
          // accountType to whatever permission level the invite allows.
          accountType: 'alpha_pending',
        } as UserProfile1;
      }

      return { uid: currentUser.uid, ...userDoc.data() } as UserProfile1;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Error loading user profile');
    }
  },
);

export const searchObjectives = createAsyncThunk<
  Objective1[], // Return type
  { query: string }, // Parameter type
  { rejectValue: string }
>('map3Data/searchObjectives', async ({ query: searchQuery }, { rejectWithValue }) => {
  try {
    const q = query(
      collection(db, 'objectives'),
      where('text', '>=', searchQuery),
      where('text', '<=', searchQuery + '\uf8ff'),
    );

    const querySnapshot = await getDocs(q);
    const results: Objective1[] = [];
    querySnapshot.forEach((doc) => {
      results.push(doc.data() as Objective1);
    });

    return results;
  } catch (error) {
    console.error('Error searching objectives:', error);
    return rejectWithValue(error instanceof Error ? error.message : 'Failed to search objectives');
  }
});

export const fetchSignUpLink = createAsyncThunk<
  SignUpLink1 | null,
  string,
  { rejectValue: string }
>('map3Data/fetchSignUpLink', async (signUpCode, { rejectWithValue }) => {
  try {
    console.log('Fetching signup link with code:', signUpCode);
    const signUpLinkDoc = await getDoc(doc(db, 'signUpLinks', signUpCode));

    if (!signUpLinkDoc.exists()) {
      console.error('Signup link not found in database:', signUpCode);
      console.log('Tried to fetch from path:', `signUpLinks/${signUpCode}`);
      return rejectWithValue('Invalid signup link');
    }

    const linkData = signUpLinkDoc.data() as SignUpLink1;
    console.log('Found signup link data:', linkData);

    // Validation checks
    if (new Date() > new Date(linkData.expirationDate)) {
      console.error('Signup link expired:', linkData.expirationDate);
      return rejectWithValue('This signup link has expired');
    }

    if (linkData.status === 'accepted') {
      console.error('Signup link already used:', linkData.status);
      return rejectWithValue('This signup link has already been used');
    }

    // Return the link data without updating it
    // The update should be handled by map3PendingSlice instead
    return linkData;
  } catch (error) {
    console.error('Error in fetchSignUpLink:', error);
    return rejectWithValue(handleAsyncError(error, 'fetchSignUpLink'));
  }
});

export const lookupUserByEmail = createAsyncThunk<
  UserLookupResult,
  string,
  { rejectValue: string }
>('map3Data/lookupUserByEmail', async (email: string) => {
  const q = query(collection(db, 'userProfiles'), where('email', 'array-contains', email));
  const querySnapshot = await getDocs(q);

  if (!querySnapshot.empty) {
    const userData = querySnapshot.docs[0].data();
    return {
      email,
      userName: userData.userName,
      firstName: userData.firstName,
      lastName: userData.lastName,
      uid: querySnapshot.docs[0].id,
      exists: true,
    };
  }
  return { email, exists: false };
});

// Fetch thunks for reading milestone map data (following REDUX.md pattern)
export const fetchMyMilestoneMapsData = createAsyncThunk(
  'map3Data/fetchMyMilestoneMapsData',
  async (_, { rejectWithValue }) => {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return rejectWithValue('No authenticated user');
      }

      const q = query(collection(db, 'milestoneMaps'), where('owner.uid', '==', currentUser.uid));

      const querySnapshot = await getDocs(q);
      const maps: MilestoneMap1[] = [];

      querySnapshot.forEach((doc) => {
        maps.push(doc.data() as MilestoneMap1);
      });

      return maps; // Return raw Firestore data
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Error fetching milestone maps',
      );
    }
  },
);

export const fetchSharedMilestoneMapsData = createAsyncThunk(
  'map3Data/fetchSharedMilestoneMapsData',
  async (_, { rejectWithValue }) => {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return rejectWithValue('No authenticated user');
      }

      // Since Firestore array-contains requires exact object matches and we can't predict
      // the exact structure of UserWithAccessAndRole objects (they may have additional fields
      // like userName, role, joinedDate, etc.), we need to get all maps and filter client-side.
      // This is less efficient but more reliable for finding shared maps.

      // TODO: Refactor and harden once user access controls have been revised. This is not efficient or secure.
      const allMapsQuery = query(collection(db, 'milestoneMaps'));
      const snapshot = await getDocs(allMapsQuery);

      const maps: MilestoneMap1[] = [];

      snapshot.forEach((doc) => {
        const mapData = doc.data() as MilestoneMap1;

        // Check if current user is in usersWithAccess
        const hasAccess = mapData.usersWithAccess?.some((user) => user.uid === currentUser.uid);

        // Check if current user is in pendingMapInvitations
        const hasPendingInvitation = mapData.pendingMapInvitations?.some(
          (invitation) => invitation.uid === currentUser.uid,
        );

        // Check if this is not the user's own map (exclude owner's maps from shared maps)
        const isNotOwner = mapData.owner?.uid !== currentUser.uid;

        if ((hasAccess || hasPendingInvitation) && isNotOwner) {
          console.log(
            '[DEBUG] fetchSharedMilestoneMapsData: Found shared map:',
            mapData.shortId,
            mapData.title,
          );
          maps.push(mapData);
        }
      });

      console.log(
        '[DEBUG] fetchSharedMilestoneMapsData: Found',
        maps.length,
        'shared maps for user',
      );
      return maps; // Return raw Firestore data
    } catch (error) {
      console.error('[DEBUG] fetchSharedMilestoneMapsData: Error:', error);
      return rejectWithValue(
        error instanceof Error ? error.message : 'Error fetching shared milestone maps',
      );
    }
  },
);

// Add type guard for MapInvitation
function isMapInvitation(obj: any): obj is MapInvitation {
  return (
    typeof obj === 'object' &&
    (obj.uid === null || typeof obj.uid === 'string') &&
    typeof obj.email === 'string' &&
    typeof obj.invitedOn === 'string' &&
    typeof obj.role === 'string'
  );
}

export const map3DataSlice = createSlice({
  name: 'map3Data',
  initialState,
  reducers: {
    // For handling optimistic updates from map3PendingSlice
    updateObjectiveOptimistically: (state, action: PayloadAction<UpdateData<Objective1>>) => {
      const { uuid, updates } = action.payload;
      const objective = state.objectives.find((obj) => obj.uuid === uuid);
      if (objective) {
        const updatedObjective = { ...objective, ...updates };
        state.objectives = upsertItem(
          state.objectives,
          updatedObjective,
          (obj) => obj.uuid === uuid,
        );
      }
    },
    updateMilestoneMapOptimistically: (state, action: PayloadAction<UpdateData<MilestoneMap1>>) => {
      const { uuid, updates } = action.payload;
      const milestoneMap = state.milestoneMaps.find((m) => m.uuid === uuid);
      if (milestoneMap) {
        if (isMapInvitation(updates)) {
          // Handle invitation update
          const updatedInvitations = [...(milestoneMap.pendingMapInvitations || []), updates];
          const updatedMap = {
            ...milestoneMap,
            pendingMapInvitations: updatedInvitations,
          } as MilestoneMap1;
          state.milestoneMaps = upsertItem(state.milestoneMaps, updatedMap, (m) => m.uuid === uuid);
        } else {
          // Handle regular map update
          const mapUpdates = updates as Partial<MilestoneMap1>;
          const updatedMap = {
            ...milestoneMap,
            ...mapUpdates,
            // Preserve arrays from existing map if not provided in updates
            objectiveUuids: mapUpdates.objectiveUuids ?? milestoneMap.objectiveUuids,
            parentObjectiveUuids:
              mapUpdates.parentObjectiveUuids ?? milestoneMap.parentObjectiveUuids,
            usersWithAccess: mapUpdates.usersWithAccess ?? milestoneMap.usersWithAccess,
            pendingMapInvitations:
              mapUpdates.pendingMapInvitations ?? milestoneMap.pendingMapInvitations,
            // Ensure owner structure is preserved
            owner: mapUpdates.owner ?? milestoneMap.owner,
            // Ensure createdBy remains a string
            createdBy:
              typeof mapUpdates.createdBy === 'object'
                ? (mapUpdates.createdBy as any).userName
                : (mapUpdates.createdBy ?? milestoneMap.createdBy),
          } as MilestoneMap1;
          state.milestoneMaps = upsertItem(state.milestoneMaps, updatedMap, (m) => m.uuid === uuid);
        }
      }
    },
    // Real-time sync management actions
    setConnectionStatus: (state, action: PayloadAction<ConnectionStatus>) => {
      state.connectionStatus = action.payload;
      state.lastConnectionAttempt = Date.now();

      // Reset reconnect attempts on successful connection
      if (action.payload === 'connected') {
        state.reconnectAttempts = 0;
        state.syncError = null;
      }

      debugLog(`[RealTimeSync] Connection status: ${action.payload}`);
    },
    setSyncError: (state, action: PayloadAction<string>) => {
      state.syncError = action.payload;
      state.connectionStatus = 'error';
      state.reconnectAttempts += 1;

      console.error(
        `[RealTimeSync] Sync error (attempt ${state.reconnectAttempts}): ${action.payload}`,
      );
    },
    clearSyncError: (state) => {
      state.syncError = null;

      debugLog('[RealTimeSync] Sync error cleared');
    },
    // Collaboration tracking actions
    updateActiveCollaborators: (
      state,
      action: PayloadAction<{ mapUuid: string; collaborators: Collaborator[] }>,
    ) => {
      const { mapUuid, collaborators } = action.payload;
      state.activeCollaborators[mapUuid] = collaborators;
      state.lastSyncTimestamp = Date.now();

      debugLog(
        `[RealTimeSync] Updated active collaborators for map ${mapUuid}: ${collaborators.length} collaborators`,
      );
    },
    clearActiveCollaborators: (state, action: PayloadAction<{ mapUuid: string }>) => {
      const { mapUuid } = action.payload;
      delete state.activeCollaborators[mapUuid];
      state.lastSyncTimestamp = Date.now();

      debugLog(`[RealTimeSync] Cleared active collaborators for map ${mapUuid}`);
    },
    // For handling real-time updates from Firebase
    handleChange: (
      state,
      action: PayloadAction<{
        operationId: string;
        type: OperationType;
        data: UpdateData<Objective1 | MilestoneMap1 | UserProfile1 | SignUpLink1 | Human | HumanRelationship>;
        source: 'map3PendingSlice' | 'firestore';
      }>,
    ) => {
      const { type, data, operationId, source } = action.payload;
      const { uuid, updates } = data;

      // If ?debug is in the url, print updates requested to console.
      debugLog('handleChange', { type, data, operationId, source });

      switch (type) {
        case 'objective_create':
        case 'objective_update': {
          const objective = state.objectives.find((obj) => obj.uuid === uuid);
          if (type === 'objective_create' || objective) {
            // Type assertion to tell TypeScript this is an Objective1 update
            const objectiveUpdates = updates as Partial<Objective1>;
            const updatedObjective: Objective1 = objective
              ? {
                  ...objective,
                  ...objectiveUpdates,
                  // Handle nested arrays explicitly
                  results: objectiveUpdates.results ?? objective.results,
                  statuses: objectiveUpdates.statuses ?? objective.statuses,
                  comments: objectiveUpdates.comments ?? objective.comments,
                  humans: objectiveUpdates.humans ?? objective.humans,
                  // Ensure createdBy remains a string
                  createdBy:
                    typeof objectiveUpdates.createdBy === 'string'
                      ? objectiveUpdates.createdBy
                      : objective.createdBy,
                }
              : (objectiveUpdates as Objective1);

            state.objectives = upsertItem(
              state.objectives,
              updatedObjective,
              (obj) => obj.uuid === uuid,
            );
          }
          break;
        }
        case 'map_create':
        case 'map_update': {
          const map = state.milestoneMaps.find((m) => m.uuid === uuid);
          if (type === 'map_create' || map) {
            const mapUpdates = updates as Partial<MilestoneMap1>;
            const updatedMap: MilestoneMap1 = map
              ? {
                  ...map,
                  ...mapUpdates,
                  // Preserve arrays from existing map if not provided in updates
                  objectiveUuids: mapUpdates.objectiveUuids ?? map.objectiveUuids,
                  parentObjectiveUuids: mapUpdates.parentObjectiveUuids ?? map.parentObjectiveUuids,
                  usersWithAccess: mapUpdates.usersWithAccess ?? map.usersWithAccess,
                  pendingMapInvitations: mapUpdates.pendingMapInvitations ?? map.pendingMapInvitations,
                  // Ensure owner structure is preserved
                  owner: mapUpdates.owner ?? map.owner,
                  // Ensure createdBy remains a string
                  createdBy:
                    typeof mapUpdates.createdBy === 'object'
                      ? (mapUpdates.createdBy as any).userName
                      : (mapUpdates.createdBy ?? map.createdBy),
                }
              : (mapUpdates as MilestoneMap1);

            state.milestoneMaps = upsertItem(
              state.milestoneMaps,
              updatedMap,
              (m) => m.uuid === uuid,
            );
          }
          break;
        }
        case 'user_create':
        case 'user_update': {
          const userUpdates = updates as Partial<UserProfile1>;
          if (type === 'user_create') {
            // For new user creation, ensure required fields are present
            const newUser: UserProfile1 = {
              uid: userUpdates.uid || '',
              userName: userUpdates.userName || '',
              email: userUpdates.email || [],
              phone: userUpdates.phone || [],
              ...userUpdates,
            };
            state.userProfile = newUser;
          } else if (state.userProfile) {
            // For updates to existing user
            const updatedUser: UserProfile1 = {
              ...state.userProfile,
              ...userUpdates,
              // Preserve array fields
              email: userUpdates.email ?? state.userProfile.email ?? [],
              phone: userUpdates.phone ?? state.userProfile.phone ?? [],
              // Preserve optional arrays
              orgs: userUpdates.orgs ?? state.userProfile.orgs,
              accessedMilestoneMaps:
                userUpdates.accessedMilestoneMaps ?? state.userProfile.accessedMilestoneMaps,
              myMilestoneMaps: userUpdates.myMilestoneMaps ?? state.userProfile.myMilestoneMaps,
              sharedMilestoneMaps:
                userUpdates.sharedMilestoneMaps ?? state.userProfile.sharedMilestoneMaps,
            };
            state.userProfile = updatedUser;
          }
          break;
        }
        case 'objective_delete': {
          state.objectives = state.objectives.filter((objective) => objective.uuid !== uuid);
          break;
        }
        case 'user_delete': {
          state.userProfile = {
            uid: '',
            accountType: 'alpha_deactivated',
            userName: '',
          };
          break;
        }
        case 'map_delete': {
          // If the map is in the user's local cache of loaded maps, remove it.
          state.milestoneMaps = state.milestoneMaps.filter((map) => map.uuid !== uuid);
          break;
        }
        case 'signup_link_update': {
          if (state.signUpLink && state.signUpLink.signUpCode === uuid) {
            // Type assertion to ensure we only update SignUpLink1 properties
            const signUpLinkUpdates = updates as Partial<SignUpLink1>;
            state.signUpLink = {
              ...state.signUpLink,
              status: signUpLinkUpdates.status || state.signUpLink.status,
              acceptedDate: signUpLinkUpdates.acceptedDate || state.signUpLink.acceptedDate,
              attempts: signUpLinkUpdates.attempts || state.signUpLink.attempts,
              lastAttempt: signUpLinkUpdates.lastAttempt || state.signUpLink.lastAttempt,
            };
          }
          break;
        }
        case 'signup_link_create': {
          // Note: No case for signup_link_create is needed here.
          // SignUpLinks are not loaded into map3DataSlice.state until a user attempts to use them.
          // The creator of a signup link does not need to see the link in their state,
          // they only need the URL which is returned by the createSignUpLink thunk.
          // This is intentional - it keeps the state clean and focused on what each user needs to see.
          break;
        }
        case 'human_create':
        case 'human_update': {
          const humanUpdates = updates as Partial<Human>;
          const existingHuman = state.humans.find((h) => h.uuid === uuid);
          if (type === 'human_create' || existingHuman) {
            const updatedHuman: Human = existingHuman
              ? { ...existingHuman, ...humanUpdates }
              : (humanUpdates as Human);
            state.humans = upsertItem(state.humans, updatedHuman, (h) => h.uuid === uuid);
          }
          break;
        }
        case 'human_delete': {
          // Remove the Human from state
          state.humans = state.humans.filter((h) => h.uuid !== uuid);
          // Remove all HumanRelationships referencing this Human
          state.humanRelationships = state.humanRelationships.filter((r) => r.humanUuid !== uuid);
          // Remove humanUuid from Objective1.humans[] arrays
          state.objectives = state.objectives.map((obj) =>
            obj.humans?.includes(uuid)
              ? { ...obj, humans: obj.humans.filter((id) => id !== uuid) }
              : obj,
          );
          break;
        }
        case 'human_relationship_create':
        case 'human_relationship_update': {
          const relUpdates = updates as Partial<HumanRelationship>;
          const existingRel = state.humanRelationships.find((r) => r.uuid === uuid);
          if (type === 'human_relationship_create' || existingRel) {
            const updatedRel: HumanRelationship = existingRel
              ? { ...existingRel, ...relUpdates }
              : (relUpdates as HumanRelationship);
            state.humanRelationships = upsertItem(
              state.humanRelationships,
              updatedRel,
              (r) => r.uuid === uuid,
            );
          }
          break;
        }
        case 'human_relationship_delete': {
          state.humanRelationships = state.humanRelationships.filter((r) => r.uuid !== uuid);
          break;
        }

        default: {
          console.error(`Unhandled change type: ${type}`);
          break;
        }
      }
      state.lastSyncTimestamp = Date.now();
    },
    setSearchResults: (
      state,
      action: PayloadAction<{ type: 'parents' | 'children'; results: Objective1[] }>,
    ) => {
      state.searchResults[action.payload.type] = action.payload.results;
      state.lastSyncTimestamp = Date.now();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadMapDataAsync.pending, (state, action) => {
        // Only show the loading screen when the map isn't already in state.
        // If data is already present (matched by either shortId or uuid), re-fetching
        // should refresh data silently without unmounting the map UI.
        //
        // The argument to loadMapDataAsync can be either a shortId (10-char) or a
        // full UUID (36-char with dashes), depending on the call site.
        // MapOptionsDropDownMenu passes map.uuid; route loaders pass mapShortId.
        // Checking both fields prevents a false-negative that would incorrectly
        // set isLoading=true for an already-loaded map.
        const idOrShortId = action.meta.arg;
        const alreadyLoaded = state.milestoneMaps.some(
          (m) => m.shortId === idOrShortId || m.uuid === idOrShortId,
        );
        if (!alreadyLoaded) {
          state.isLoading = true;
        }
        state.error = null;
      })
      .addCase(loadMapDataAsync.fulfilled, (state, action) => {
        const { milestoneMap, objectives, dependencyRecords } = action.payload;

        state.milestoneMaps = upsertItem(
          state.milestoneMaps,
          milestoneMap,
          (m) => m.uuid === milestoneMap.uuid,
        );

        objectives.forEach((objective) => {
          state.objectives = upsertItem(
            state.objectives,
            objective,
            (obj) => obj.uuid === objective.uuid,
          );
        });

        // Update dependency records
        state.dependencyRecords = dependencyRecords;

        state.isLoading = false;
        state.lastSyncTimestamp = Date.now();
      })
      .addCase(loadMapDataAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to load map data';
      })
      .addCase(loadCurrentUserProfile.fulfilled, (state, action) => {
        state.userProfile = action.payload;
        state.lastSyncTimestamp = Date.now();
      })
      .addCase(loadCurrentUserProfile.rejected, (state) => {
        state.userProfile = initialState.userProfile;
        state.error = 'Failed to load user profile';
      })
      .addCase(searchObjectives.fulfilled, (state, action) => {
        state.searchResults.parents = action.payload;
        state.lastSyncTimestamp = Date.now();
      })
      .addCase(searchObjectives.rejected, (state, action) => {
        state.error = action.payload || 'Failed to search objectives';
      })
      .addCase(fetchMilestoneMap.fulfilled, (state, action) => {
        const milestoneMap = action.payload;
        state.milestoneMaps = upsertItem(
          state.milestoneMaps,
          milestoneMap,
          (m) => m.uuid === milestoneMap.uuid,
        );
        state.lastSyncTimestamp = Date.now();
      })
      .addCase(fetchMilestoneMapsByObjective.fulfilled, (state, action) => {
        action.payload.forEach((milestoneMap) => {
          state.milestoneMaps = upsertItem(
            state.milestoneMaps,
            milestoneMap,
            (m) => m.uuid === milestoneMap.uuid,
          );
        });
        state.lastSyncTimestamp = Date.now();
      })
      .addCase(fetchObjectives.fulfilled, (state, action) => {
        action.payload.forEach((objective) => {
          state.objectives = upsertItem(
            state.objectives,
            objective,
            (obj) => obj.uuid === objective.uuid,
          );
        });
        state.lastSyncTimestamp = Date.now();
      })
      .addCase(fetchHumansAndRelationshipsForMap.fulfilled, (state, action) => {
        const { humans, humanRelationships } = action.payload;
        humans.forEach((human) => {
          state.humans = upsertItem(state.humans, human, (h) => h.uuid === human.uuid);
        });
        humanRelationships.forEach((rel) => {
          state.humanRelationships = upsertItem(
            state.humanRelationships,
            rel,
            (r) => r.uuid === rel.uuid,
          );
        });
        state.lastSyncTimestamp = Date.now();
      })
      .addCase(fetchSignUpLink.fulfilled, (state, action) => {
        state.signUpLink = action.payload;
        state.lastSyncTimestamp = Date.now();
      })
      .addCase(fetchSignUpLink.rejected, (state, action) => {
        state.signUpLink = null;
        state.error = action.payload || 'Invalid signup code';
      })
      // Collaboration thunk handlers
      .addCase(startMilestoneMapListener.fulfilled, (state, action) => {
        const { listenerId } = action.payload;
        if (!state.activeListenerIds.includes(listenerId)) {
          state.activeListenerIds.push(listenerId);
        }
      })
      .addCase(startObjectivesListener.fulfilled, (state, action) => {
        const { listenerId } = action.payload;
        if (!state.activeListenerIds.includes(listenerId)) {
          state.activeListenerIds.push(listenerId);
        }
      })
      .addCase(cleanupMapListeners.fulfilled, (state, action) => {
        const { mapUuid } = action.payload;
        const mapListenerId = getListenerId('milestoneMap', mapUuid);
        const objectivesListenerId = getListenerId('objectives', mapUuid);
        const collaboratorsListenerId = getListenerId('collaborators', mapUuid);
        const humansListenerId = getListenerId('humans', mapUuid);
        const humanRelationshipsListenerId = getListenerId('humanRelationships', mapUuid);
        state.activeListenerIds = state.activeListenerIds.filter(
          (id) =>
            id !== mapListenerId &&
            id !== objectivesListenerId &&
            id !== collaboratorsListenerId &&
            id !== humansListenerId &&
            id !== humanRelationshipsListenerId,
        );

        // Clear collaboration data for this map
        delete state.activeCollaborators[mapUuid];
      })
      .addCase(cleanupAllCollaborationListeners.fulfilled, (state) => {
        state.activeListenerIds = [];
      })
      // Collaborators thunk handlers
      .addCase(startCollaboratorsListener.fulfilled, (state, action) => {
        const { listenerId } = action.payload;
        if (!state.activeListenerIds.includes(listenerId)) {
          state.activeListenerIds.push(listenerId);
        }
      })
      .addCase(stopCollaboratorsListener.fulfilled, (state, action) => {
        const { listenerId } = action.payload;
        state.activeListenerIds = state.activeListenerIds.filter((id) => id !== listenerId);
      })
      // Humans thunk handlers
      .addCase(startHumansListener.fulfilled, (state, action) => {
        const { listenerId } = action.payload;
        if (!state.activeListenerIds.includes(listenerId)) {
          state.activeListenerIds.push(listenerId);
        }
      })
      .addCase(stopHumansListener.fulfilled, (state, action) => {
        const { listenerId } = action.payload;
        state.activeListenerIds = state.activeListenerIds.filter((id) => id !== listenerId);
      })
      // HumanRelationships thunk handlers
      .addCase(startHumanRelationshipsListener.fulfilled, (state, action) => {
        const { listenerId } = action.payload;
        if (!state.activeListenerIds.includes(listenerId)) {
          state.activeListenerIds.push(listenerId);
        }
      })
      .addCase(stopHumanRelationshipsListener.fulfilled, (state, action) => {
        const { listenerId } = action.payload;
        state.activeListenerIds = state.activeListenerIds.filter((id) => id !== listenerId);
      });
  },
});

export const {
  updateObjectiveOptimistically,
  updateMilestoneMapOptimistically,
  handleChange,
  setSearchResults,
  // Real-time sync actions
  setConnectionStatus,
  setSyncError,
  clearSyncError,
  // Collaboration tracking actions
  updateActiveCollaborators,
  clearActiveCollaborators,
} = map3DataSlice.actions;

export default map3DataSlice.reducer;

// Note: Cleanup functionality moved to collaboration thunks
// Use cleanupMapListeners from collaborationThunks.js instead
